import { useState } from "react";

export default function AuthRegisterUser({ state, onChange, onSuccess, onError, authService }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Cognito enforces usernames matching [\p{L}\p{M}\p{S}\p{N}\p{P}]+
  // This excludes spaces, separators, and control characters.
  const usernameRegex = /^[\p{L}\p{M}\p{S}\p{N}\p{P}]+$/u;

  // Translate Cognito error codes into user-friendly messages
  const friendlyError = (err) => {
    switch (err?.code || err?.name) {
      case "UsernameExistsException":
        return "This username is already taken. Please choose a different one.";
      case "InvalidPasswordException":
        return err.message || "Password does not meet the requirements.";
      case "InvalidParameterException":
        return err.message || "One or more fields are invalid. Please check your input.";
      case "PreSignUp":
        // Pre Sign-Up Lambda errors are wrapped by Cognito with a prefix
        // e.g. "PreSignUp failed with error An account with this email address already exists."
        return err.message?.replace(/^PreSignUp failed with error\s*/, "") || err.message;
      default:
        // Cognito wraps Pre Sign-Up Lambda errors with a UserLambdaValidationException code
        if (err?.code === "UserLambdaValidationException" || err?.name === "UserLambdaValidationException") {
          return err.message?.replace(/^PreSignUp failed with error\s*/, "") || err.message;
        }
        return err.message || "An error occurred during sign up.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate username format
    if (!usernameRegex.test(state.username)) {
      setError("Username cannot contain spaces or special whitespace characters");
      return;
    }

    // Validate passwords match
    if (state.password !== state.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // For Cognito, often the username and email are the same
      const attributes = {
        email: state.email || state.username,
        name: state.name,
      };

      const result = await authService.register(state.username, state.password, attributes);
      setLoading(false);

      if (typeof onSuccess === "function") {
        localStorage.setItem("register", state.username);
        onSuccess({ action: "confirm", response: result });
      }
    } catch (err) {
      setLoading(false);
      const errorMessage = friendlyError(err);
      setError(errorMessage);

      if (typeof onError === "function") {
        onError(err);
      }
    }
  };

  return (
    <div className="mx-auto w-full max-w-md">
      <form onSubmit={handleSubmit} className="mb-4 rounded px-8 pt-6 pb-8">
        <h2 className="text-center">Create Account</h2>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-bold" htmlFor="name">
            Name
          </label>
          <input
            className="input-text"
            id="name"
            type="text"
            placeholder="Full Name"
            value={state.name || ""}
            onChange={(e) => onChange("name", e.target.value)}
            required
          />
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-bold" htmlFor="email">
            Email
          </label>
          <input
            className="input-text"
            id="email"
            type="email"
            placeholder="you@example.com"
            value={state.email || ""}
            onChange={(e) => onChange("email", e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-gray-400">We'll send a verification code to this address.</p>
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-bold" htmlFor="username">
            Choose a Username
          </label>
          <input
            className="input-text"
            id="username"
            type="text"
            placeholder="e.g. jsmith or john.smith123"
            value={state.username || ""}
            onChange={(e) => onChange("username", e.target.value)}
            autoComplete="username"
            required
          />
          <p className="mt-1 text-xs text-gray-400">
            No spaces &mdash; only use letters, numbers, dashes and dots.
          </p>
        </div>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-bold" htmlFor="password">
            Password
          </label>
          <input
            className="input-text"
            id="password"
            type="password"
            placeholder="Enter password"
            value={state.password || ""}
            onChange={(e) => onChange("password", e.target.value)}
            autoComplete="new-password"
            required
          />
          <p className="mt-1 text-xs text-gray-400">Must be at least 8 characters.</p>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-bold" htmlFor="confirmPassword">
            Confirm Password
          </label>
          <input
            className="input-text"
            id="confirmPassword"
            type="password"
            placeholder="Re-enter your password"
            value={state.confirmPassword || ""}
            onChange={(e) => onChange("confirmPassword", e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {error && (
          <div
            className="relative mt-4 mb-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
            role="alert"
          >
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end">
          <button
            className={`bg-proof-blue focus:shadow-outline rounded px-4 py-2 font-bold text-white hover:opacity-80 focus:outline-none ${loading ? "cursor-not-allowed opacity-50" : ""}`}
            type="submit"
            disabled={loading}
          >
            {loading ? "Signing up..." : "Sign Up"}
          </button>
        </div>
      </form>
    </div>
  );
}
